import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getEmployees } from '../services/profileService';
import { toArray } from '../utils/api';

const STORAGE_KEY = 'selected_employee';
const SelectedEmployeeContext = createContext(null);

function loadPersistedSelection(user) {
  if (!user?.username) return null;
  try {
    const sessionStored = sessionStorage.getItem(STORAGE_KEY);
    if (sessionStored) {
      const parsed = JSON.parse(sessionStored);
      if (parsed?.ownerUsername === user.username) {
        return parsed.employee || null;
      }
    }

    // Clean up any older cross-session storage format so admins default to self.
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

export function SelectedEmployeeProvider({ children }) {
  const { isAdmin, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployeeState] = useState(() => isAdmin ? loadPersistedSelection(user) : null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeesFetchState, setEmployeesFetchState] = useState('idle');
  const employeesRequestRef = useRef(null);

  const setSelectedEmployee = useCallback((emp) => {
    setSelectedEmployeeState(emp);
    try {
      if (emp) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          ownerUsername: user?.username,
          employee: emp,
        }));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Ignore storage unavailability and keep in-memory selection.
    }
  }, [user?.username]);

  const fetchEmployees = useCallback(async () => {
    if (!isAdmin) return [];
    if (employeesRequestRef.current) return employeesRequestRef.current;
    setLoadingEmployees(true);
    employeesRequestRef.current = (async () => {
      try {
        const data = await getEmployees();
        const safeEmployees = toArray(data);
        setEmployees(safeEmployees);
        setEmployeesFetchState('success');
        return safeEmployees;
      } catch {
        setEmployees([]);
        setEmployeesFetchState('error');
        return [];
      } finally {
        setLoadingEmployees(false);
        employeesRequestRef.current = null;
      }
    })();

    return employeesRequestRef.current;
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    } else {
      // Non-admin: clear any stale selection
      setSelectedEmployeeState(null);
      setEmployeesFetchState('idle');
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isAdmin, fetchEmployees]);

  useEffect(() => {
    if (!isAdmin || !user?.username) return;
    setSelectedEmployeeState(loadPersistedSelection(user));
  }, [isAdmin, user]);

  const clearSelection = useCallback(() => {
    setSelectedEmployee(null);
  }, [setSelectedEmployee]);

  useEffect(() => {
    if (!isAdmin || loadingEmployees || employeesFetchState !== 'success') return;

    const persistedSelectionStillExists = selectedEmployee
      ? employees.some((emp) => emp.username === selectedEmployee.username)
      : false;

    if (!selectedEmployee || persistedSelectionStillExists) return;

    clearSelection();
  }, [isAdmin, loadingEmployees, employeesFetchState, employees, selectedEmployee, clearSelection]);

  // The username to use for data-fetching: selected employee's username or null (self)
  const targetUsername = selectedEmployee?.username || null;

  const value = useMemo(
    () => ({
      employees,
      selectedEmployee,
      setSelectedEmployee,
      clearSelection,
      loadingEmployees,
      fetchEmployees,
      targetUsername,
    }),
    [employees, selectedEmployee, setSelectedEmployee, clearSelection, loadingEmployees, fetchEmployees, targetUsername]
  );

  return (
    <SelectedEmployeeContext.Provider value={value}>
      {children}
    </SelectedEmployeeContext.Provider>
  );
}

export function useSelectedEmployee() {
  const context = useContext(SelectedEmployeeContext);
  if (!context) {
    throw new Error('useSelectedEmployee must be used within a SelectedEmployeeProvider');
  }
  return context;
}
